<script setup>
import { ref } from 'vue';
import { useToast } from '@/plugins/toast';
import { useAjaxForm } from '@/utils/form';

import Parser from '@/components/Secondary/Content/Parser.vue';
import ListingStatusBadge from './ListingStatusBadge.vue';

const props = defineProps({
    listing: Object
});

const emit = defineEmits(['refresh']);

const share = () => {
    const baseUrl = import.meta.env.VITE_APP_URL;
    navigator.share({
        url: `${baseUrl}/marketplace/listings/${props.listing.id}`,
    }).catch(err => console.error(err));
}

const saved = ref(props.listing.user_save_exists);
const saveForm = useAjaxForm();
const publishForm = useAjaxForm();
const toast = useToast();

const save = () => {
    const url = `/api/marketplace/listings/${props.listing.id}/save`;
    if (!saved.value) {
        saveForm.post(url, {
            onSuccess: () => {
                saved.value = true;
                emit('refresh');
                toast.success('Saved successfully')
            }
        });
    } else {
        saveForm.delete(url, {
            onSuccess: () => {
                saved.value = false;
                emit('refresh');
            }
        });
    }
}

const statusText = {
    draft: 'Draft',
    pending: 'In Review',
    active: 'Active',
    sold: 'Sold',
    rejected: 'Rejected',
};

const statusColor = {
    draft: 'black',
    pending: 'warning',
    active: 'primary',
    sold: 'info',
    rejected: 'danger',
};

const publish = () => {
    confirm({ title: 'Publish listing?', description: 'Your listing will be reviewed and made public after approval.'})
        .then(() => {
            publishForm.post(`/api/marketplace/listings/${props.listing.id}/publish`, {
                onSuccess: () => {
                    toast.success('Your listing is now pending review.');
                    emit('refresh');
                }
            });
        });
}
</script>

<template>
    <div>
        <div class="p-4">
            <div v-if="listing.status !== 'active'" class="mb-1">
                <ListingStatusBadge :status="listing.status" />
            </div>
            <h2 class="text-xl font-semibold">
                {{ listing.title }}
            </h2>
            <p class="text-lg font-medium">{{ $money(listing.price) }}</p>
            <p class="text-sm text-muted" v-if="listing.published_at">Listed {{ $dayjs(listing.published_at).fromNow() }}</p>
            <p v-if="listing.location" class="text-sm text-muted">{{ listing.location.state }}</p>

            <div class="flex items-center mt-2 gap-3">
                <app-button
                    @click="publish"
                    v-if="listing.seller.is_me && listing.status === 'draft'"
                    color="primary"
                    capitalize
                    full
                    icon="telegram">Publish</app-button>
                <app-button v-else-if="!listing.seller.is_me && ['active', 'sold'].includes(listing.status)" color="primary" capitalize full icon="message-text-outline">Message</app-button>
                <template v-if="listing.status === 'active' || listing.status === 'sold'">
                    <app-icon-button :disabled="saveForm.processing" :class="{
                        'animate-pulse': saveForm.processing
                    }" aria-label="save" icon="bookmark" sm button-style="filled-tonal" :selected="saved" @click="save" />
                    <app-icon-button aria-label="share" icon="share-variant" sm button-style="filled-tonal" @click="share" />
                </template>
                <app-icon-button
                    v-if="listing.seller.is_me && ['draft', 'active'].includes(listing.status)"
                    aria-label="edit"
                    icon="pencil"
                    sm
                    button-style="filled-tonal"
                    @click="$router.push({
                        name: 'marketplace.listing.edit',
                        params: { id: listing.id }
                    })" />
            </div>

            <div class="mt-4 border-t border-white-foreground/10 py-4">
                <h2 class="font-semibold text-lg">Description</h2>
                <div>
                    <app-line-clamp :lines="3">
                        <Parser :content="listing.description" :exclude-parsers="['mention', 'link']" />
                    </app-line-clamp>
                </div>
            </div>

            <div class="mt-4 border-t border-white-foreground/10 py-4">
                <h2 class="font-semibold text-lg">Seller Information</h2>
                <RouterLink class="flex items-center gap-4 py-2" :to="{ name: 'marketplace.sellers.show', params: { id: listing.seller.id } }">
                    <div class="self-start shrink-0">
                        <app-avatar :src="listing.seller.profile.image.url" size="lg" />
                    </div>
                    <div>
                        <div class="flex items-center gap-2">
                            <div class="text-lg leading-tight font-medium select-text">{{ listing.seller.profile.name }}</div>
                            <div v-if="listing.seller.profile.verified" class="shrink-0 text-primary">
                                <icon-verified-user class="size-4" />
                            </div>
                        </div>
                        <div class="text-muted text-sm">{{ listing.seller.profile.verified ? 'Verified' : 'Unverified' }} Seller</div>
                    </div>
                </RouterLink>
            </div>
        </div>
    </div>
</template>
